(function () {
  'use strict';

  var DRAFT_STORAGE_KEY = 'tior-product-inquiry-draft-v1';

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  }

  onReady(function () {
    var modal = document.querySelector('[data-tior-inquiry-modal]');
    if (!modal) return;

    var form = modal.querySelector('[data-tior-inquiry-form]');
    var success = modal.querySelector('[data-tior-inquiry-success]');
    var status = modal.querySelector('[data-tior-inquiry-status]');
    var submit = modal.querySelector('[data-tior-inquiry-submit]');
    var productImage = modal.querySelector('[data-tior-inquiry-product-image]');
    var productTitle = modal.querySelector('[data-tior-inquiry-product-title]');
    var productMeta = modal.querySelector('[data-tior-inquiry-product-meta]');
    var recovery = modal.querySelector('[data-tior-inquiry-recovery]');
    var retry = modal.querySelector('[data-tior-inquiry-retry]');
    var fallback = modal.querySelector('[data-tior-inquiry-fallback]');
    var activeTrigger = null;
    var activeProducts = [];
    var activeIdempotencyKey = '';

    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var trigger = event.target.closest('[data-tior-product-inquiry-trigger]');
      if (!trigger) return;
      event.preventDefault();
      openModal(trigger);
    });

    modal.querySelectorAll('[data-tior-inquiry-close]').forEach(function (button) {
      button.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', function (event) {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        closeModal();
        return;
      }
      if (event.key === 'Tab') trapFocus(event);
    });

    form.addEventListener('input', saveDraft);
    retry.addEventListener('click', function () {
      recovery.hidden = true;
      form.requestSubmit();
    });

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      status.textContent = '';

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      if (!activeProducts.length) {
        status.textContent = 'Product information is unavailable. Please refresh and try again.';
        return;
      }

      var formData = new FormData(form);
      activeIdempotencyKey = activeIdempotencyKey || createIdempotencyKey('product-inquiry');
      saveDraft();
      recovery.hidden = true;
      status.textContent = 'Sending your inquiry securely...';
      setSubmitting(true);
      try {
        var response = await fetch('/apps/tior-ai/consultation/apply', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requestType: 'PRODUCT_INQUIRY',
            idempotencyKey: activeIdempotencyKey,
            source: activeTrigger.dataset.inquirySource || 'PRODUCT_DETAIL',
            name: String(formData.get('name') || '').trim(),
            email: String(formData.get('email') || '').trim(),
            phone: String(formData.get('phone') || '').trim(),
            message: String(formData.get('message') || '').trim(),
            website: String(formData.get('website') || ''),
            pageUrl: window.location.href,
            products: activeProducts,
            customer: {
              name: String(formData.get('name') || '').trim(),
              email: String(formData.get('email') || '').trim(),
              phone: String(formData.get('phone') || '').trim()
            }
          })
        });
        var payload = await response.json().catch(function () { return {}; });
        if (!response.ok) throw new Error(payload.message || 'Unable to send your inquiry.');

        clearDraft();
        status.textContent = '';
        form.hidden = true;
        success.hidden = false;
        success.querySelector('button').focus();
      } catch (error) {
        saveDraft();
        status.textContent = 'Your draft is saved. We could not send it yet; try again or continue through the contact form.';
        recovery.hidden = false;
        fallback.href = buildFallbackUrl();
      } finally {
        setSubmitting(false);
      }
    });

    function openModal(trigger) {
      activeTrigger = trigger;
      activeProducts = buildProductSnapshots(trigger);
      form.hidden = false;
      success.hidden = true;
      status.textContent = '';
      recovery.hidden = true;
      renderProducts(activeProducts);
      restoreDraft();
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('tior-inquiry-modal-open');
      window.setTimeout(function () {
        var firstField = form.querySelector('input[name="name"]');
        if (firstField) firstField.focus();
      }, 0);
    }

    function closeModal() {
      if (modal.hidden) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('tior-inquiry-modal-open');
      if (activeTrigger) activeTrigger.focus();
      activeTrigger = null;
      activeProducts = [];
    }

    function buildProductSnapshots(trigger) {
      var scriptId = String(trigger.dataset.inquiryProductsScript || '').trim();
      if (scriptId) {
        var script = document.getElementById(scriptId);
        if (script) {
          try {
            var products = JSON.parse(script.textContent || '[]');
            if (Array.isArray(products) && products.length) {
              return products.map(normalizeProductSnapshot).filter(function (product) {
                return product.productId || product.variantId || product.title;
              });
            }
          } catch (error) {
            console.error('Unable to read cart inquiry products.', error);
          }
        }
      }

      var product = buildProductSnapshot(trigger);
      return product ? [product] : [];
    }

    function normalizeProductSnapshot(product) {
      var snapshot = product && typeof product === 'object' ? product : {};
      return {
        productId: String(snapshot.productId || ''),
        variantId: String(snapshot.variantId || ''),
        sku: String(snapshot.sku || ''),
        title: String(snapshot.title || 'Product inquiry'),
        variantTitle: normalizeVariantTitle(snapshot.variantTitle),
        vendor: String(snapshot.vendor || ''),
        imageUrl: absoluteUrl(snapshot.imageUrl),
        productUrl: absoluteUrl(snapshot.productUrl),
        quantity: Math.max(1, Math.min(999, Math.floor(Number(snapshot.quantity) || 1))),
        price: String(snapshot.price || ''),
        priceLabel: snapshot.priceLabel ? 'Request a Quote' : ''
      };
    }

    function buildProductSnapshot(trigger) {
      var container = trigger.closest('.shopify-section, section, product-info, .product') || document;
      var variantInput = container.querySelector('form[action*="/cart/add"] input[name="id"], product-form input[name="id"]');
      var selectedVariant = new URL(window.location.href).searchParams.get('variant');
      var variantId = (variantInput && variantInput.value) || selectedVariant || trigger.dataset.variantId || '';
      var variant = findVariant(container, variantId);
      var productUrl = absoluteUrl(trigger.dataset.productUrl);

      return {
        productId: String(trigger.dataset.productId || ''),
        variantId: String(variantId || ''),
        sku: String((variant && variant.sku) || trigger.dataset.productSku || ''),
        title: String(trigger.dataset.productTitle || 'Product inquiry'),
        variantTitle: normalizeVariantTitle((variant && variant.title) || trigger.dataset.variantTitle),
        vendor: String(trigger.dataset.productVendor || ''),
        imageUrl: absoluteUrl(getVariantImage(variant) || trigger.dataset.productImage),
        productUrl: withVariant(productUrl, variantId),
        quantity: getSelectedQuantity(container),
        price: String(trigger.dataset.productPrice || ''),
        priceLabel: trigger.dataset.priceLabel ? 'Request a Quote' : ''
      };
    }

    function findVariant(container, variantId) {
      if (!variantId) return null;
      var dataScript = container.querySelector(
        'variant-selects script[type="application/json"], variant-radios script[type="application/json"]'
      );
      if (!dataScript) return null;
      try {
        var variants = JSON.parse(dataScript.textContent || '[]');
        return variants.find(function (variant) {
          return String(variant.id || '') === String(variantId);
        }) || null;
      } catch (error) {
        return null;
      }
    }

    function getVariantImage(variant) {
      if (!variant || !variant.featured_image) return '';
      return variant.featured_image.src || variant.featured_image.url || '';
    }

    function getSelectedQuantity(container) {
      var quantityInput = container.querySelector('form[action*="/cart/add"] input[name="quantity"], product-form input[name="quantity"]');
      var quantity = Number(quantityInput && quantityInput.value);
      return Number.isFinite(quantity) ? Math.max(1, Math.min(999, Math.floor(quantity))) : 1;
    }

    function withVariant(productUrl, variantId) {
      if (!productUrl || !variantId) return productUrl;
      try {
        var url = new URL(productUrl);
        url.searchParams.set('variant', variantId);
        return url.toString();
      } catch (error) {
        return productUrl;
      }
    }

    function renderProducts(products) {
      var product = products[0];
      if (!product) return;
      if (products.length === 1) {
        productTitle.textContent = product.title;
        productMeta.textContent = [product.vendor, product.variantTitle, product.priceLabel || product.price]
          .filter(Boolean)
          .join(' / ');
      } else {
        var visibleTitles = products.slice(0, 3).map(function (item) { return item.title; });
        var remaining = products.length - visibleTitles.length;
        productTitle.textContent = products.length + ' products selected';
        productMeta.textContent = visibleTitles.join(', ') + (remaining > 0 ? ' +' + remaining + ' more' : '');
      }
      if (product.imageUrl) {
        productImage.src = product.imageUrl;
        productImage.alt = products.length === 1 ? product.title : products.length + ' selected products';
        productImage.hidden = false;
      } else {
        productImage.removeAttribute('src');
        productImage.alt = '';
        productImage.hidden = true;
      }
    }

    function setSubmitting(isSubmitting) {
      submit.disabled = isSubmitting;
      submit.setAttribute('aria-busy', String(isSubmitting));
      submit.textContent = isSubmitting ? 'Sending…' : 'Send inquiry';
    }

    function createIdempotencyKey(prefix) {
      var randomPart = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + Math.random().toString(36).slice(2);
      return prefix + ':' + randomPart;
    }

    function productSignature() {
      return activeProducts.map(function (product) {
        return [product.productId, product.variantId, product.quantity].join(':');
      }).join('|');
    }

    function saveDraft() {
      if (!activeProducts.length) return;
      var formData = new FormData(form);
      try {
        window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
          signature: productSignature(),
          idempotencyKey: activeIdempotencyKey || '',
          name: String(formData.get('name') || ''),
          email: String(formData.get('email') || ''),
          phone: String(formData.get('phone') || ''),
          message: String(formData.get('message') || ''),
          savedAt: Date.now()
        }));
      } catch (error) {
        // Submission still works when browser storage is unavailable.
      }
    }

    function restoreDraft() {
      try {
        var draft = JSON.parse(window.localStorage.getItem(DRAFT_STORAGE_KEY) || 'null');
        if (!draft || draft.signature !== productSignature()) {
          activeIdempotencyKey = '';
          return;
        }
        ['name', 'email', 'phone', 'message'].forEach(function (name) {
          var field = form.elements.namedItem(name);
          if (field && draft[name]) field.value = String(draft[name]);
        });
        activeIdempotencyKey = String(draft.idempotencyKey || '');
        if (draft.message || draft.phone || draft.email) status.textContent = 'Your saved draft has been restored.';
      } catch (error) {
        activeIdempotencyKey = '';
      }
    }

    function clearDraft() {
      activeIdempotencyKey = '';
      try {
        window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch (error) {}
    }

    function buildFallbackUrl() {
      var product = activeProducts[0] || {};
      var url = new URL('/pages/contact', window.location.origin);
      url.searchParams.set('source', 'product_inquiry_fallback');
      url.searchParams.set('product', product.title || 'Product inquiry');
      if (product.productId) url.searchParams.set('productId', product.productId);
      if (product.variantId) url.searchParams.set('variantId', product.variantId);
      try {
        window.sessionStorage.setItem('tior-product-inquiry-fallback-v1', JSON.stringify(activeProducts.slice(0, 20)));
      } catch (error) {}
      return url.pathname + url.search;
    }

    function normalizeVariantTitle(value) {
      var title = String(value || '').trim();
      return title && title.toLowerCase() !== 'default title' ? title : '';
    }

    function absoluteUrl(value) {
      if (!value) return '';
      try {
        return new URL(String(value), window.location.origin).toString();
      } catch (error) {
        return '';
      }
    }

    function trapFocus(event) {
      var focusable = Array.prototype.slice.call(modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (element) { return !element.hidden; });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
})();
